import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { DialogFooter } from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Info } from "lucide-react";
import { useFundManagers } from "@/hooks/useFundManagers";
import {
  pensionPlanFormSchema,
  PensionPlanFormSchema,
} from "./pensionPlanForm.schema";
import { useState } from "react";
import { useCurrencyList } from "@/hooks/useCurrencyList";
import {PensionPlanAccount} from "@/integrations/supabase/pension.models.ts";

const COUNTRIES = [
  "Kenya",
  "Nigeria",
  "South Africa",
  "Ghana",
  "Tanzania",
  "Uganda",
  "United States",
  "United Kingdom",
  "Canada",
  "Germany",
  "France",
  "India",
  "Japan",
  "China",
  "Australia",
  "Brazil",
  "Mexico",
  "Spain",
  "Italy",
  "Netherlands",
];

interface PensionPlanFormProps {
  pensionPlan?: PensionPlanAccount;
  onSave: (data: PensionPlanFormData) => void | Promise<void>;
  onCancel: () => void;
  isSubmitting?: boolean;
}

/**
 * PensionPlanForm component for creating and editing pension plans.
 * Supports three pension types: Government, Occupational, and Individual.
 * Conditionally renders fields based on selected pension type.
 *
 * @param pensionPlan - Optional existing pension plan for editing
 * @param onSave - Callback function when form is submitted with valid data
 * @param onCancel - Callback function when form is cancelled
 * @param isSubmitting - External submission state indicator
 *
 * @example
 * <PensionPlanForm
 *   pensionPlan={existingPlan}
 *   onSave={handleSave}
 *   onCancel={handleCancel}
 * />
 */
export function PensionPlanForm({
  pensionPlan,
  onSave,
  onCancel,
  isSubmitting = false,
}: PensionPlanFormProps) {
  const [showNewFundManagerInput, setShowNewFundManagerInput] = useState(false);
  const {
    data: availableCurrencies,
    isLoading: isLoadingCurrencies,
    error: errorLoadingCurrencies,
  } = useCurrencyList();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting: formIsSubmitting },
  } = useForm<PensionPlanFormSchema>({
    resolver: zodResolver(pensionPlanFormSchema),
    defaultValues: {
      fundName: pensionPlan?.fund_name ?? "",
      accountNumber: pensionPlan?.account_number ?? "",
      currency: pensionPlan?.currency ?? "USD",
      interestRate: pensionPlan?.interest_rate ?? 0,
      startingAmount: pensionPlan?.starting_amount ?? 0,
      notes: pensionPlan?.notes ?? "",
      description: pensionPlan?.description ?? "",
      pensionType: pensionPlan?.pension_metadata?.[0].pension_type ?? "government",
      country: pensionPlan?.pension_metadata?.[0].country ?? "",
      fund_manager_id: pensionPlan?.pension_metadata?.[0].fund_manager_id ?? "",
      newFundManagerName: "",
      fundManager: pensionPlan?.fund_manager ?? "",
      employerName: pensionPlan?.pension_metadata?.[0].employer_name ?? "",
      employerContributionPercentage:
        pensionPlan?.pension_metadata?.[0].employer_contribution_percentage ?? 0,
      employeeContributionPercentage:
        pensionPlan?.pension_metadata?.[0].employee_contribution_percentage ?? 0,
    },
  });

  const pensionType = watch("pensionType") as
    | "government"
    | "occupational"
    | "individual";
  const country = watch("country") as string | undefined;
  const currency = watch("currency") as string;
  const interestRate = watch("interestRate") as number;
  const startingAmount = watch("startingAmount") as number;
  const fundManagerId = watch("fundManagerId") as string | undefined;

  // Fetch all fund managers (country filtering removed — table is now shared/global)
  const { data: fundManagers = [], isLoading: isLoadingFundManagers } =
    useFundManagers();

  const onSubmit = async (data: PensionPlanFormSchema) => {
    await onSave(data as PensionPlanFormData);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="pension_type">Pension Plan Type *</Label>
        <Select
          value={pensionType}
          onValueChange={(value) => {
            setValue(
              "pensionType",
              value as "government" | "occupational" | "individual",
            );
            // Reset type-specific fields when type changes
            setValue("country", "");
            setValue("fundManagerId", "");
            setValue("newFundManagerName", "");
            setValue("fundManager", "");
            setValue("employerName", "");
            setValue("employerContributionPercentage", 0);
            setValue("employeeContributionPercentage", 0);
            setShowNewFundManagerInput(false);
          }}>
          <SelectTrigger id="pension_type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="government">Government Pension Plan</SelectItem>
            <SelectItem value="occupational">
              Occupational Pension Plan
            </SelectItem>
            <SelectItem value="individual">Individual Pension Plan</SelectItem>
          </SelectContent>
        </Select>
        {errors.pensionType && (
          <p className="text-sm text-destructive">
            {String(errors.pensionType.message)}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="fund_name">Fund Name *</Label>
        <Input
          id="fund_name"
          {...register("fundName")}
          placeholder="e.g. National Pension Fund"
          maxLength={100}
        />
        {errors.fundName && (
          <p className="text-sm text-destructive">
            {String(errors.fundName.message)}
          </p>
        )}
      </div>

      {/* Government Pension Fields */}
      {pensionType === "government" && (
        <>
          <div className="space-y-2">
            <Label htmlFor="country">Country *</Label>
            <Select
              value={country}
              onValueChange={(value) => {
                setValue("country", value);
                // Reset fund manager when country changes
                setValue("fundManagerId", "");
                setValue("newFundManagerName", "");
                setShowNewFundManagerInput(false);
              }}>
              <SelectTrigger id="country">
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
              <SelectContent>
                {COUNTRIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.country && (
              <p className="text-sm text-destructive">
                {String(errors.country.message)}
              </p>
            )}
          </div>

          {country && (
            <div className="space-y-2">
              <Label htmlFor="fund_manager_id">Fund Manager *</Label>
              {!showNewFundManagerInput ? (
                <>
                  <Select
                    value={fundManagerId}
                    onValueChange={(value) => {
                      if (value === "add_new") {
                        setShowNewFundManagerInput(true);
                        setValue("fundManagerId", "");
                      } else {
                        setValue("fundManagerId", value);
                      }
                    }}>
                    <SelectTrigger id="fund_manager_id">
                      <SelectValue
                        placeholder={
                          isLoadingFundManagers
                            ? "Loading..."
                            : "Select fund manager"
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {fundManagers.map((fm) => (
                        <SelectItem key={fm.id} value={fm.id}>
                          {fm.name}
                        </SelectItem>
                      ))}
                      <SelectItem value="add_new">
                        + Add new fund manager
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.fundManagerId && (
                    <p className="text-sm text-destructive">
                      {String(errors.fundManagerId.message)}
                    </p>
                  )}
                </>
              ) : (
                <>
                  <div className="flex gap-2">
                    <Input
                      id="new_fund_manager_name"
                      {...register("newFundManagerName")}
                      placeholder="Enter fund manager name"
                      maxLength={100}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowNewFundManagerInput(false);
                        setValue("newFundManagerName", "");
                      }}>
                      Cancel
                    </Button>
                  </div>
                  {errors.newFundManagerName && (
                    <p className="text-sm text-destructive">
                      {String(errors.newFundManagerName.message)}
                    </p>
                  )}
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* Occupational Pension Fields */}
      {pensionType === "occupational" && (
        <>
          <div className="space-y-2">
            <Label htmlFor="fund_manager">Fund Manager *</Label>
            <Input
              id="fund_manager"
              {...register("fundManager")}
              placeholder="e.g. ABC Pension Fund"
              maxLength={100}
            />
            {errors.fundManager && (
              <p className="text-sm text-destructive">
                {String(errors.fundManager.message)}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <Label htmlFor="employer_name">Employer Name *</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="h-4 w-4 text-muted-foreground" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p className="font-semibold mb-2">
                      Occupational Pension Sub-types:
                    </p>
                    <ul className="space-y-1 text-sm">
                      <li>
                        <strong>Defined Contribution:</strong> Contributions are
                        fixed, benefits depend on investment performance
                      </li>
                      <li>
                        <strong>Defined Benefit:</strong> Benefits are
                        predetermined based on salary and years of service
                      </li>
                      <li>
                        <strong>Occupational Pension:</strong> General
                        employer-sponsored retirement plan
                      </li>
                    </ul>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            <Input
              id="employer_name"
              {...register("employerName")}
              placeholder="e.g. Acme Corporation"
              maxLength={100}
            />
            {errors.employerName && (
              <p className="text-sm text-destructive">
                {String(errors.employerName.message)}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="employer_contribution_percentage">
                Employer Contribution (%) *
              </Label>
              <Input
                id="employer_contribution_percentage"
                type="number"
                step="0.01"
                min="0"
                max="100"
                {...register("employerContributionPercentage", {
                  valueAsNumber: true,
                })}
                placeholder="e.g. 5"
              />
              {errors.employerContributionPercentage && (
                <p className="text-sm text-destructive">
                  {String(errors.employerContributionPercentage.message)}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="employee_contribution_percentage">
                Employee Contribution (%) *
              </Label>
              <Input
                id="employee_contribution_percentage"
                type="number"
                step="0.01"
                min="0"
                max="100"
                {...register("employeeContributionPercentage", {
                  valueAsNumber: true,
                })}
                placeholder="e.g. 5"
              />
              {errors.employeeContributionPercentage && (
                <p className="text-sm text-destructive">
                  {String(errors.employeeContributionPercentage.message)}
                </p>
              )}
            </div>
          </div>
        </>
      )}

      {/* Individual Pension Fields */}
      {pensionType === "individual" && (
        <div className="space-y-2">
          <Label htmlFor="fund_manager">Fund Manager *</Label>
          <Input
            id="fund_manager"
            {...register("fundManager")}
            placeholder="e.g. Vanguard"
            maxLength={100}
          />
          {errors.fundManager && (
            <p className="text-sm text-destructive">
              {String(errors.fundManager.message)}
            </p>
          )}
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="account_number">Account Number *</Label>
        <Input
          id="account_number"
          {...register("accountNumber")}
          placeholder="e.g. PEN-123456"
          maxLength={50}
        />
        {errors.accountNumber && (
          <p className="text-sm text-destructive">
            {String(errors.accountNumber.message)}
          </p>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="currency">Currency *</Label>
          <Select
            value={currency}
            onValueChange={(value) => setValue("currency", value)}>
            <SelectTrigger id="currency">
              <SelectValue />
            </SelectTrigger>
            {isLoadingCurrencies && "Loading Currencies"}
            {!isLoadingCurrencies && !errorLoadingCurrencies ? (
              <SelectContent>
                {availableCurrencies.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            ) : (
              "Failed to load currencies"
            )}
          </Select>
          {errors.currency && (
            <p className="text-sm text-destructive">
              {String(errors.currency.message)}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="interest_rate">Interest Rate (% p.a.) *</Label>
          <Input
            id="interest_rate"
            type="number"
            step="0.01"
            min="0"
            max="100"
            {...register("interestRate", { valueAsNumber: true })}
            placeholder="e.g. 5.5"
          />
          {errors.interestRate && (
            <p className="text-sm text-destructive">
              {String(errors.interestRate.message)}
            </p>
          )}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="starting_amount">Starting Amount *</Label>
        <Input
          id="starting_amount"
          type="number"
          step="0.01"
          min="0.01"
          {...register("startingAmount", { valueAsNumber: true })}
          placeholder="e.g. 10000"
        />
        {errors.startingAmount && (
          <p className="text-sm text-destructive">
            {String(errors.startingAmount.message)}
          </p>
        )}
        {interestRate > 0 && startingAmount > 0 && (
          <div className="text-xs text-muted-foreground space-y-1">
            <p>
              Estimated yearly interest:{" "}
              {(
                startingAmount *
                (Math.pow(1 + interestRate / 100 / 12, 12) - 1)
              ).toLocaleString(undefined, {
                style: "currency",
                currency,
              })}
            </p>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Input
          id="description"
          {...register("description")}
          placeholder="Brief description of the pension plan"
          maxLength={500}
        />
        {errors.description && (
          <p className="text-sm text-destructive">
            {String(errors.description.message)}
          </p>
        )}
      </div>

      <div className="space-y-2">
        <Label htmlFor="notes">Notes</Label>
        <Textarea
          id="notes"
          {...register("notes")}
          placeholder="Additional notes..."
          rows={3}
          maxLength={500}
        />
        {errors.notes && (
          <p className="text-sm text-destructive">
            {String(errors.notes.message)}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isSubmitting || formIsSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting || formIsSubmitting}>
          {isSubmitting || formIsSubmitting
            ? "Saving..."
            : pensionPlan
              ? "Update"
              : "Create"}
        </Button>
      </DialogFooter>
    </form>
  );
}
